import isEqual from 'fast-deep-equal/react'
import { formatFilterPill } from './formatters/format-filter-pills'
import {
  getFormattedBodyType,
  getFormattedColour,
  getFormattedDoors,
  getFormattedDriveType,
  getFormattedEngineSizeCcRange,
  getFormattedFeatures,
  getFormattedFinanceType,
  getFormattedFuelType,
  getFormattedMake,
  getFormattedMileage,
  getFormattedModel,
  getFormattedPriceRange,
  getFormattedPriceRangeAndTags,
  getFormattedSeats,
  getFormattedTags,
  getFormattedGearbox,
  getFormattedVariant,
  getFormattedYearRange,
  formatFilterOptionValue,
} from './formatters'
import { options } from './formatter-options'
import { extractMappingsFromMakesFacet } from '../../utils'
import { getFormattedStoreLocations } from './formatters/store-location-formatter'
import {
  getStoreDataFromAggregation,
  type AgeData,
  type FilterFacets,
  type GetVehiclesResponse,
} from '@cinch-nx/data-search'
import { useUsedCarsData } from '../use-used-cars-data/use-used-cars-data'

let previousSearchResultsData: GetVehiclesResponse | undefined
let previousUseMonthly = false

export const getAgeDataFromVehicleYearFacets = (
  vehicleYearFacets?: FilterFacets['vehicleYear'],
): AgeData[] => {
  if (!vehicleYearFacets) {
    return []
  }

  return Object.keys(vehicleYearFacets)
    .filter((key) => Boolean(Number.parseInt(key)))
    .map((key) => ({ label: key.toString(), value: Number.parseInt(key) }))
}

const summaryFormatters = {
  getFormattedMake: (value?: string) =>
    getFormattedMake(value, options.mappings),
  getFormattedModel: (value?: string) =>
    getFormattedModel(value, options.mappings),
  getFormattedVariant: (value?: string) =>
    getFormattedVariant(value, options.mappings),
  getFormattedPriceRange: (from?: number, to?: number) =>
    getFormattedPriceRange(from, to, options.useMonthly),
  getFormattedYearRange: (from?: number, to?: number) =>
    getFormattedYearRange(from, to, options.ageData),
  getFormattedMileage,
  getFormattedFuelType,
  getFormattedColour,
  getFormattedBodyType,
  getFormattedGearbox,
  getFormattedEngineSizeCcRange,
  getFormattedDoors,
  getFormattedSeats,
  getFormattedDriveType,
  getFormattedTags,
  getFormattedFeatures,
  getFormattedPriceRangeAndTags: (
    from?: number,
    to?: number,
    tags?: string,
    financeType?: string,
    // eslint-disable-next-line arrow-body-style
  ) => {
    return getFormattedPriceRangeAndTags(
      getFormattedPriceRange(
        from,
        to,
        options.useMonthly,
        financeType ? [financeType] : [],
      ),
      getFormattedTags(tags),
      getFormattedFinanceType(financeType),
    )
  },
  getFormattedStoreLocations,
  getFormattedFinanceType,
}

export type SummaryFormatters = typeof summaryFormatters

export function useSearchFiltersFormatting() {
  const {
    searchParams: { useMonthly },
    searchResults,
  } = useUsedCarsData()

  if (searchResults && !isEqual(searchResults, previousSearchResultsData)) {
    options.ageData = getAgeDataFromVehicleYearFacets(
      searchResults?.facets?.vehicleYear,
    )
    options.mappings = extractMappingsFromMakesFacet(
      searchResults?.facets?.makes ?? [],
    )
    options.storeData = getStoreDataFromAggregation(
      searchResults?.facets?.store ?? {},
    )
    options.facets = searchResults?.facets

    previousSearchResultsData = searchResults
  }

  if (!isEqual(useMonthly, previousUseMonthly)) {
    options.useMonthly = useMonthly
    previousUseMonthly = useMonthly
  }

  return {
    ageData: options.ageData,
    formatFilterPill,
    summaryFormatters,
    formatFilterOptionValue,
  }
}
